// Admin console: Subscription Plans catalog. Plan versions are immutable —
// this screen only ever creates a NEW version, it never edits one in place,
// so existing subscribers' promised limits never change silently (concept
// doc §3.1/§8).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { adminApi } from '../api/adminClient';
import type { PlanVersionInput } from '../api/types';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatEUR } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { Button, Card } from '../components/ui';
import { ByteInput } from '../components/ByteInput';
import { Field } from '../components/Field';
import { useToast } from '../components/Toast';
import { IconClose, IconLayers, IconPlus } from '../components/Icons';

const EMPTY_VERSION: PlanVersionInput = {
  writeSlaHours: 96,
  minStoragePerArchiveBytes: 7 * 1024 ** 3,
  purchasedStorageBytes: 0,
  rateLimitWriteBytesPerDay: 1024 ** 4,
  rateLimitReadBytesPerDay: 1024 ** 4,
  verificationLevel: '',
  verificationFrequency: '',
  retrievalOptions: [],
  priceWriteEurPerGb: 0,
  priceReadFreeLifetimeCount: 0,
  priceReadEurPerReadout: 0,
  priceStorageEurPerGbYear: 0,
  physicalPackaging: '',
  ttfbReadingHours: 96,
  ttfbSiteId: '',
  eccProfile: '',
  storageLocationSiteId: '',
  minBillableObjectBytes: 0,
  minBillableReadBytes: 0,
  mediumRedundancyPercent: 0,
  writeTriggers: [],
  features: {},
};

export function AdminPlans() {
  const { t } = useTranslation();
  const lang = useLang();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [newVersionFor, setNewVersionFor] = useState<{ id: string; name: string; base: PlanVersionInput | null } | null>(null);
  const plans = useAsync(() => adminApi.adminPlans(), []);

  return (
    <>
      <PageHeader
        title={t('admin.plans.title')}
        subtitle={t('admin.plans.subtitle')}
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            <IconPlus width={18} height={18} />
            {t('admin.plans.create')}
          </Button>
        }
      />

      <Card>
        <ApiState status={plans.status} error={plans.error} data={plans.status === 'error' ? null : plans.data} onRetry={plans.reload}>
          {(d) =>
            d.items.length === 0 ? (
              <EmptyState title={t('admin.plans.empty')} icon={<IconLayers width={26} height={26} />} />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>{t('common.name')}</th>
                      <th className="num">{t('admin.plans.colVersion')}</th>
                      <th>{t('admin.fields.writeSlaHours')}</th>
                      <th>{t('admin.fields.rateLimitWriteBytesPerDay')}</th>
                      <th className="num">{t('admin.fields.priceWriteEurPerGb')}</th>
                      <th className="num">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((p) => (
                      <tr key={p.id}>
                        <td className="cell-strong">{p.name}</td>
                        <td className="num">{p.latestVersion ? `v${p.latestVersion.version}` : '—'}</td>
                        <td>{p.latestVersion ? `${p.latestVersion.writeSlaHours}h` : '—'}</td>
                        <td>{p.latestVersion ? `${(p.latestVersion.rateLimitWriteBytesPerDay / 1024 ** 3).toFixed(0)} GB/d` : '—'}</td>
                        <td className="num">{p.latestVersion ? formatEUR(p.latestVersion.priceWriteEurPerGb, lang) : '—'}</td>
                        <td className="num">
                          <Button variant="secondary" size="sm" onClick={() =>
                              setNewVersionFor({
                                id: p.id,
                                name: p.name,
                                // Seed from the current version: a new version is
                                // almost always a small change to the live one,
                                // and retyping 20 fields invites mistakes.
                                base: p.latestVersion
                                  ? ({ ...p.latestVersion } as unknown as PlanVersionInput)
                                  : null,
                              })
                            }>
                            {t('admin.plans.newVersion')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </ApiState>
      </Card>

      {creating ? (
        <PlanVersionDialog
          title={t('admin.plans.createTitle')}
          needsName
          onClose={() => setCreating(false)}
          onSubmit={async (name, version) => {
            await adminApi.adminCreatePlan(name!, version);
          }}
          onDone={() => {
            setCreating(false);
            toast.success(t('admin.plans.created'));
            plans.reload();
          }}
        />
      ) : null}

      {newVersionFor ? (
        <PlanVersionDialog
          title={t('admin.plans.newVersionTitle', { name: newVersionFor.name })}
          base={newVersionFor.base}
          onClose={() => setNewVersionFor(null)}
          onSubmit={async (_name, version) => {
            await adminApi.adminCreatePlanVersion(newVersionFor.id, version);
          }}
          onDone={() => {
            setNewVersionFor(null);
            toast.success(t('admin.plans.versionCreated'));
            plans.reload();
          }}
        />
      ) : null}
    </>
  );
}

// PlanVersionDialog is shared by "create plan" (needsName) and "new version of
// an existing plan" — the field set is identical either way.
function PlanVersionDialog({
  title,
  needsName,
  base,
  onClose,
  onSubmit,
  onDone,
}: {
  title: string;
  needsName?: boolean;
  /** Values to start from; the current version when creating a new one. */
  base?: PlanVersionInput | null;
  onClose: () => void;
  onSubmit: (name: string | undefined, version: PlanVersionInput) => Promise<void>;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [v, setV] = useState<PlanVersionInput>(base ?? EMPTY_VERSION);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const num = (key: keyof PlanVersionInput) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV((cur) => ({ ...cur, [key]: Number(e.target.value) }));
  // Byte fields are entered as amount + unit; a cleared field means 0 here
  // because a plan version always carries a concrete number.
  const bytes = (key: keyof PlanVersionInput) => (v: string) =>
    setV((cur) => ({ ...cur, [key]: v === '' ? 0 : Number(v) }));
  const str = (key: keyof PlanVersionInput) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV((cur) => ({ ...cur, [key]: e.target.value }));
  const list = (key: keyof PlanVersionInput) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setV((cur) => ({ ...cur, [key]: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (needsName && !name.trim()) {
      setError(t('common.required'));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit(needsName ? name.trim() : undefined, v);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="plan-title" style={{ maxWidth: 640 }}>
        <div className="row between">
          <h2 className="dialog__title" id="plan-title">
            {title}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <IconClose width={16} height={16} />
          </button>
        </div>
        <div style={{ marginTop: 16, maxHeight: '60vh', overflowY: 'auto' }}>
          {needsName ? (
            <Field label={t('admin.plans.nameLabel')} required error={error}>
              {(p) => <input className="input" {...p} value={name} autoFocus onChange={(e) => setName(e.target.value)} />}
            </Field>
          ) : null}
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Field label={t('admin.fields.writeSlaHours')}>
              {(p) => <input className="input" type="number" {...p} value={v.writeSlaHours} onChange={num('writeSlaHours')} />}
            </Field>
            <Field label={t('admin.fields.minStoragePerArchiveBytes')} hint={t('admin.plans.bytesHint')}>
              {(p) => <ByteInput fieldProps={p} value={String(v.minStoragePerArchiveBytes)} onChange={bytes('minStoragePerArchiveBytes')} />}
            </Field>
            <Field label={t('admin.fields.purchasedStorageBytes')} hint={t('admin.plans.purchasedHint')}>
              {(p) => <ByteInput fieldProps={p} value={String(v.purchasedStorageBytes)} onChange={bytes('purchasedStorageBytes')} />}
            </Field>
            <Field label={t('admin.fields.rateLimitWriteBytesPerDay')} hint={t('admin.plans.bytesHint')}>
              {(p) => <ByteInput fieldProps={p} value={String(v.rateLimitWriteBytesPerDay)} onChange={bytes('rateLimitWriteBytesPerDay')} />}
            </Field>
            <Field label={t('admin.fields.rateLimitReadBytesPerDay')} hint={t('admin.plans.bytesHint')}>
              {(p) => <ByteInput fieldProps={p} value={String(v.rateLimitReadBytesPerDay)} onChange={bytes('rateLimitReadBytesPerDay')} />}
            </Field>
            <Field label={t('admin.fields.verificationLevel')}>
              {(p) => <input className="input" {...p} value={v.verificationLevel} onChange={str('verificationLevel')} />}
            </Field>
            <Field label={t('admin.fields.verificationFrequency')}>
              {(p) => <input className="input" {...p} value={v.verificationFrequency} onChange={str('verificationFrequency')} />}
            </Field>
            <Field label={t('admin.fields.retrievalOptions')} hint={t('admin.plans.listHint')}>
              {(p) => <input className="input" {...p} defaultValue={v.retrievalOptions.join(', ')} onBlur={list('retrievalOptions')} />}
            </Field>
            <Field label={t('admin.fields.writeTriggers')} hint={t('admin.plans.listHint')}>
              {(p) => <input className="input" {...p} defaultValue={v.writeTriggers.join(', ')} onBlur={list('writeTriggers')} />}
            </Field>
            <Field label={t('admin.fields.priceWriteEurPerGb')}>
              {(p) => <input className="input" type="number" step="any" {...p} value={v.priceWriteEurPerGb} onChange={num('priceWriteEurPerGb')} />}
            </Field>
            <Field label={t('admin.fields.priceReadFreeLifetimeCount')}>
              {(p) => <input className="input" type="number" {...p} value={v.priceReadFreeLifetimeCount} onChange={num('priceReadFreeLifetimeCount')} />}
            </Field>
            <Field label={t('admin.fields.priceReadEurPerReadout')}>
              {(p) => <input className="input" type="number" step="any" {...p} value={v.priceReadEurPerReadout} onChange={num('priceReadEurPerReadout')} />}
            </Field>
            <Field label={t('admin.fields.priceStorageEurPerGbYear')}>
              {(p) => <input className="input" type="number" step="any" {...p} value={v.priceStorageEurPerGbYear} onChange={num('priceStorageEurPerGbYear')} />}
            </Field>
            <Field label={t('admin.fields.physicalPackaging')}>
              {(p) => <input className="input" {...p} value={v.physicalPackaging} onChange={str('physicalPackaging')} />}
            </Field>
            <Field label={t('admin.fields.ttfbReadingHours')}>
              {(p) => <input className="input" type="number" {...p} value={v.ttfbReadingHours} onChange={num('ttfbReadingHours')} />}
            </Field>
            <Field label={t('admin.fields.ttfbSiteId')}>
              {(p) => <input className="input" {...p} value={v.ttfbSiteId} onChange={str('ttfbSiteId')} />}
            </Field>
            <Field label={t('admin.fields.eccProfile')}>
              {(p) => <input className="input" {...p} value={v.eccProfile} onChange={str('eccProfile')} />}
            </Field>
            <Field label={t('admin.fields.storageLocationSiteId')}>
              {(p) => <input className="input" {...p} value={v.storageLocationSiteId} onChange={str('storageLocationSiteId')} />}
            </Field>
            <Field label={t('admin.fields.minBillableObjectBytes')} hint={t('admin.plans.bytesHint')}>
              {(p) => <ByteInput fieldProps={p} value={String(v.minBillableObjectBytes)} onChange={bytes('minBillableObjectBytes')} />}
            </Field>
            <Field label={t('admin.fields.minBillableReadBytes')} hint={t('admin.plans.bytesHint')}>
              {(p) => <ByteInput fieldProps={p} value={String(v.minBillableReadBytes)} onChange={bytes('minBillableReadBytes')} />}
            </Field>
            <Field label={t('admin.fields.mediumRedundancyPercent')}>
              {(p) => <input className="input" type="number" step="any" {...p} value={v.mediumRedundancyPercent} onChange={num('mediumRedundancyPercent')} />}
            </Field>
          </div>
        </div>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
